import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, ArrowRightIcon, LinkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

// Simple API client for Zahara
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? 'https://plantedhire.com/api' : 'http://localhost:5001/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const Zahara: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [nominalCode, setNominalCode] = useState('');
  const [nominalCodeId, setNominalCodeId] = useState('');
  const [nominalDescription, setNominalDescription] = useState('');
  const [taxCode, setTaxCode] = useState('');
  const [taxCodeId, setTaxCodeId] = useState('');
  const [taxDescription, setTaxDescription] = useState('');
  const [taxPercentage, setTaxPercentage] = useState('');
  const [costCode, setCostCode] = useState('');
  const [costCodeId, setCostCodeId] = useState('');
  const [costDescription, setCostDescription] = useState('');
  const [divisionCode, setDivisionCode] = useState('');
  const [divisionCodeId, setDivisionCodeId] = useState('');
  const [divisionDescription, setDivisionDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Supplier matching state
  const [plantedSuppliers, setPlantedSuppliers] = useState<any[]>([]);
  const [zaharaSuppliers, setZaharaSuppliers] = useState<any[]>([]);
  const [supplierMatches, setSupplierMatches] = useState<any[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedPlantedSupplier, setSelectedPlantedSupplier] = useState<string>('');
  const [selectedZaharaSupplier, setSelectedZaharaSupplier] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/zahara/settings');
      const data = response.data;
      setApiKey(data.api_key || '');
      setNominalCode(data.nominal_code || '');
      setNominalCodeId(data.nominal_code_id || '');
      setNominalDescription(data.nominal_description || '');
      setTaxCode(data.tax_code || '');
      setTaxCodeId(data.tax_code_id || '');
      setTaxDescription(data.tax_description || '');
      setTaxPercentage(data.tax_percentage || '');
      setCostCode(data.cost_code || '');
      setCostCodeId(data.cost_code_id || '');
      setCostDescription(data.cost_description || '');
      setDivisionCode(data.division_code || '');
      setDivisionCodeId(data.division_code_id || '');
      setDivisionDescription(data.division_description || '');
    } catch (error) {
      console.error('Error loading Zahara settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleLookupNominalCode = async () => {
    if (!nominalCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter a nominal code' });
      return;
    }

    try {
      setLookupLoading(true);
      setMessage(null);
      
      const response = await api.post('/zahara/lookup-nominal-code', {
        code: nominalCode.trim()
      });
      
      if (response.data.success) {
        const { id, code, description } = response.data.data;
        setNominalCodeId(id.toString());
        setNominalDescription(description);
        setMessage({ type: 'success', text: `Found: ${code} - ${description}` });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Nominal code not found' });
      }
    } catch (error) {
      console.error('Error looking up nominal code:', error);
      const errorMessage = error.response?.data?.error || 'Failed to lookup nominal code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleLookupTaxCode = async () => {
    if (!taxCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter a tax code' });
      return;
    }

    try {
      setLookupLoading(true);
      setMessage(null);
      
      const response = await api.post('/zahara/lookup-tax-code', {
        code: taxCode.trim()
      });
      
      if (response.data.success) {
        const { id, code, codeName, taxPercentage } = response.data.data;
        setTaxCodeId(id.toString());
        setTaxDescription(codeName);
        setTaxPercentage(taxPercentage.toString());
        setMessage({ type: 'success', text: `Found: ${code} - ${codeName} (${taxPercentage}%)` });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Tax code not found' });
      }
    } catch (error) {
      console.error('Error looking up tax code:', error);
      const errorMessage = error.response?.data?.error || 'Failed to lookup tax code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleLookupCostCode = async () => {
    if (!costCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter a cost code' });
      return;
    }

    try {
      setLookupLoading(true);
      setMessage(null);
      
      const response = await api.post('/zahara/lookup-cost-code', {
        code: costCode.trim()
      });
      
      if (response.data.success) {
        const { id, code, description } = response.data.data;
        setCostCodeId(id.toString());
        setCostDescription(description);
        setMessage({ type: 'success', text: `Found: ${code} - ${description}` });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Cost code not found' });
      }
    } catch (error) {
      console.error('Error looking up cost code:', error);
      const errorMessage = error.response?.data?.error || 'Failed to lookup cost code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleLookupDivision = async () => {
    if (!divisionCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter a division name' });
      return;
    }

    try {
      setLookupLoading(true);
      setMessage(null);
      
      const response = await api.post('/zahara/lookup-division', {
        name: divisionCode.trim()
      });
      
      if (response.data.success) {
        const { id, code, description } = response.data.data;
        setDivisionCodeId(id.toString());
        setDivisionDescription(description);
        setMessage({ type: 'success', text: `Found: ${code} - ${description}` });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Division not found' });
      }
    } catch (error: any) {
      console.error('Error looking up division:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to lookup division' });
    } finally {
      setLookupLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setMessage(null);
      
      console.log('Loading suppliers...');
      
      // Load Planted suppliers
      console.log('Loading Planted suppliers...');
      const plantedResponse = await api.get('/suppliers');
      console.log('Planted suppliers response:', plantedResponse.data);
      setPlantedSuppliers(plantedResponse.data.suppliers || []);
      
      // Load Zahara suppliers
      console.log('Loading Zahara suppliers...');
      const zaharaResponse = await api.get('/zahara/suppliers?type=Hire');
      console.log('Zahara suppliers response:', zaharaResponse.data);
      if (zaharaResponse.data.success) {
        setZaharaSuppliers(zaharaResponse.data.data);
      } else {
        setMessage({ type: 'error', text: zaharaResponse.data.error || 'Failed to load Zahara suppliers' });
      }
      
      // Load existing matches
      console.log('Loading existing matches...');
      const matchesResponse = await api.get('/zahara/supplier-matches');
      console.log('Matches response:', matchesResponse.data);
      if (matchesResponse.data.success) {
        setSupplierMatches(matchesResponse.data.data);
      }
      
      setMessage({ type: 'success', text: `Loaded ${plantedResponse.data?.suppliers?.length || 0} Planted suppliers and ${zaharaResponse.data.success ? zaharaResponse.data.data.length : 0} Zahara suppliers` });
    } catch (error) {
      console.error('Error loading suppliers:', error);
      const errorMessage = error.response?.data?.error || 'Failed to load suppliers';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleSupplierMatch = async () => {
    if (!selectedPlantedSupplier || !selectedZaharaSupplier) {
      setMessage({ type: 'error', text: 'Please select both suppliers to match' });
      return;
    }

    try {
      const plantedSupplier = plantedSuppliers.find(s => s.id === selectedPlantedSupplier);
      const zaharaSupplier = zaharaSuppliers.find(s => s.supplierId.toString() === selectedZaharaSupplier);

      if (!plantedSupplier || !zaharaSupplier) {
        setMessage({ type: 'error', text: 'Selected suppliers not found' });
        return;
      }

      await api.post('/zahara/supplier-matches', {
        supplier_id: selectedPlantedSupplier,
        zahara_supplier_id: zaharaSupplier.supplierId,
        zahara_nominal_id: zaharaSupplier.defaultNominalCodeId,
        zahara_default_tax_code_id: zaharaSupplier.defaultTaxCodeId,
        zahara_cost_code_id: zaharaSupplier.defaultCostCodeId,
        zahara_division_id: zaharaSupplier.defaultDivisionId
      });

      setMessage({ type: 'success', text: `Matched ${plantedSupplier.name} with ${zaharaSupplier.supplierName}` });
      
      // Reload matches
      const matchesResponse = await api.get('/zahara/supplier-matches');
      if (matchesResponse.data.success) {
        setSupplierMatches(matchesResponse.data.data);
      }
      
      // Clear selections
      setSelectedPlantedSupplier('');
      setSelectedZaharaSupplier('');
    } catch (error) {
      console.error('Error matching suppliers:', error);
      setMessage({ type: 'error', text: 'Failed to match suppliers' });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      await api.put('/zahara/settings', {
        api_key: apiKey,
        cost_code: costCode,
        cost_code_id: costCodeId,
        cost_description: costDescription,
        nominal_code: nominalCode,
        nominal_code_id: nominalCodeId,
        nominal_description: nominalDescription,
        tax_code: taxCode,
        tax_code_id: taxCodeId,
        tax_description: taxDescription,
        tax_percentage: taxPercentage,
        division_code: divisionCode,
        division_code_id: divisionCodeId,
        division_description: divisionDescription,
        is_active: !!apiKey
      });
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving Zahara settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-gray-500">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Zahara Integration
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Configure your Zahara API key and default codes for integration
              </p>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`rounded-md p-4 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {message.type === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    message.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {message.text}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Zahara Settings Form */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="space-y-6">
              {/* API Key Section */}
              <div>
                <label htmlFor="api_key" className="label">
                  API Key *
                </label>
                <input
                  type="text"
                  id="api_key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="mt-1 block w-full input"
                  placeholder="Enter your Zahara API key"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Your API key will be securely stored and used to authenticate with Zahara
                </p>
              </div>

              {/* Nominal Code Section */}
              <div>
                <label htmlFor="nominal_code" className="label">
                  Default Nominal Code
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="text"
                    id="nominal_code"
                    value={nominalCode}
                    onChange={(e) => setNominalCode(e.target.value)}
                    className="input"
                    placeholder="e.g., 7030"
                  />
                  <button
                    onClick={handleLookupNominalCode}
                    disabled={lookupLoading || !nominalCode.trim() || !apiKey.trim()}
                    className="btn btn-outline"
                  >
                    {lookupLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      'Lookup'
                    )}
                  </button>
                  {nominalDescription && (
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      <span>{nominalCode} - {nominalDescription}</span>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the nominal code to lookup its ID and description from Zahara
                </p>
              </div>

              {/* Tax Code Section */}
              <div>
                <label htmlFor="tax_code" className="label">
                  Default Tax Code
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="text"
                    id="tax_code"
                    value={taxCode}
                    onChange={(e) => setTaxCode(e.target.value)}
                    className="input"
                    placeholder="e.g., VAT20"
                  />
                  <button
                    onClick={handleLookupTaxCode}
                    disabled={lookupLoading || !taxCode.trim() || !apiKey.trim()}
                    className="btn btn-outline"
                  >
                    {lookupLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      'Lookup'
                    )}
                  </button>
                  {taxDescription && (
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      <span>{taxCode} - {taxDescription} ({taxPercentage}%)</span>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the tax code to lookup its ID, description, and percentage from Zahara
                </p>
              </div>

              {/* Cost Code Section */}
              <div>
                <label htmlFor="cost_code" className="label">
                  Default Cost Code
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="text"
                    id="cost_code"
                    value={costCode}
                    onChange={(e) => setCostCode(e.target.value)}
                    className="input"
                    placeholder="e.g., BRICKS"
                  />
                  <button
                    onClick={handleLookupCostCode}
                    disabled={lookupLoading || !costCode.trim() || !apiKey.trim()}
                    className="btn btn-outline"
                  >
                    {lookupLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      'Lookup'
                    )}
                  </button>
                  {costDescription && (
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      <span>{costCode} - {costDescription}</span>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the cost code to lookup its ID and description from Zahara
                </p>
              </div>

              {/* Division Section */}
              <div>
                <label htmlFor="division_code" className="label">
                  Default Division
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="text"
                    id="division_code"
                    value={divisionCode}
                    onChange={(e) => setDivisionCode(e.target.value)}
                    className="input"
                    placeholder="e.g., Jobs"
                  />
                  <button
                    onClick={handleLookupDivision}
                    disabled={lookupLoading || !divisionCode.trim() || !apiKey.trim()}
                    className="btn btn-outline"
                  >
                    {lookupLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      'Lookup'
                    )}
                  </button>
                  {divisionDescription && (
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      <span>{divisionCode} - {divisionDescription}</span>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the division name to lookup its ID and description from Zahara
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || !apiKey.trim()}
                className="btn btn-primary"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : null}
                Save Settings
              </button>
            </div>
          </div>
        </div>

        {/* Supplier Matching Section */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mt-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Supplier Matching</h3>
              <p className="mt-1 text-sm text-gray-500">
                Match your Planted suppliers with Zahara suppliers for seamless integration
              </p>
            </div>
            <button
              onClick={loadSuppliers}
              disabled={loadingSuppliers || !apiKey.trim()}
              className="btn btn-secondary"
            >
              {loadingSuppliers ? (
                <div className="animate-spin"></div>
              ) : (
                <LinkIcon className="h-4 w-4 mr-2" />
              )}
              Load Suppliers
            </button>
          </div>

          {/* Loading State */}
          {loadingSuppliers && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading suppliers...</p>
            </div>
          )}

          {/* No Suppliers State */}
          {!loadingSuppliers && plantedSuppliers.length === 0 && zaharaSuppliers.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Click "Load Suppliers" to fetch suppliers from both systems</p>
            </div>
          )}

          {/* Supplier Matching Interface */}
          {!loadingSuppliers && plantedSuppliers.length > 0 && zaharaSuppliers.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Planted Suppliers */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Planted Suppliers</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {plantedSuppliers.map((supplier) => (
                    <div
                      key={supplier.id}
                      className={`p-2 rounded cursor-pointer text-sm ${
                        selectedPlantedSupplier === supplier.id
                          ? 'bg-primary-50 border border-primary-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedPlantedSupplier(supplier.id)}
                    >
                      <div className="font-medium text-gray-900">{supplier.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Match Button */}
              <div className="flex items-center justify-center">
                <button
                  onClick={handleSupplierMatch}
                  disabled={!selectedPlantedSupplier || !selectedZaharaSupplier}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  Match
                </button>
              </div>

              {/* Zahara Suppliers */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Zahara Suppliers</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {zaharaSuppliers.map((supplier) => (
                    <div
                      key={supplier.supplierId}
                      className={`p-2 rounded cursor-pointer text-sm ${
                        selectedZaharaSupplier === supplier.supplierId.toString()
                          ? 'bg-primary-50 border border-primary-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedZaharaSupplier(supplier.supplierId.toString())}
                    >
                      <div className="font-medium text-gray-900">{supplier.supplierName}</div>
                      <div className="text-gray-500 text-xs">
                        Ref: {supplier.referenceNumber} | {supplier.defaultNominalCode} | {supplier.defaultTaxCode}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Existing Matches */}
          {supplierMatches.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Existing Matches</h4>
              <div className="space-y-2">
                {supplierMatches.map((match) => (
                  <div key={match.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                      <div>
                        <div className="font-medium text-gray-900">{match.supplier_name}</div>
                        <div className="text-sm text-gray-500">Zahara ID: {match.zahara_supplier_id}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      Nominal: {match.zahara_nominal_id} | Tax: {match.zahara_default_tax_code_id}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};
